import React, { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate, Link, useParams } from 'react-router-dom'
import axios from 'axios'
import plus from '../../../../../assets/certificates/AddParameter/blue-plus.svg'
import save from '../../../../../assets/certificates/AddParameter/save.svg'
import trash from '../../../../../assets/equipment/Tables/trash.svg'
import rectangle from '../../../../../assets/equipment/rectangle.svg'
import {
  MainContainer,
  StyledCard,
  StyledCardHeader,
  FormGrid,
  StyledFormGroup,
  MaintenanceServiceWrapper,
  ToggleSwitchContainer,
  SwitchLabel,
  Slider,
  SwitchInput,
  AddMaintenanceButton,
  StyledMaintenanceItem,
  VerificationGroup,
  PeriodicidadeGroup,
  DeleteButton,
  ActionButtons,
  CancelButton,
  SaveButton,
  SubHeaderContainer,
  SectionName,
  LeftSide,
  BreadcrumbsContainer,
  CardTitle,
  ToggleSwitchLabelText,
  NameGroup,
  DropdownContainer,
  DropdownHeader,
  DropdownHeaderSelectedItems,
  DropdownSelectedItemTag,
  DropdownList,
  DropdownListItem,
} from '../RegisterEquipment/style'

interface MaintenanceScheduleItem {
  id: number
  maintenanceName: string
  periodicityValue: string
  periodicityUnit: '' | 'm' | 'km' | 'days' | 'hours' | 'months'
  isVerificationEnabled: boolean
  verificationValue: string
  verificationUnit: '' | 'hours' | 'days' | 'months'
  isInternalService: boolean
  selectedServiceProviders: readonly string[]
}

interface FormState {
  code: string
  name: string
  model: string
  manufacturer: string
  year: string
  type: string
  dimension: string
  weight: string
  location: string
  costPerHour: string
  capacity: string
  selectedServiceProviders: readonly string[]
}

interface Option {
  value: string
  label: string
}

interface BackendPeriodicMaintenanceItem {
  id?: number
  maintenanceName?: string
  periodicityValue?: number
  periodicityUnit?: '' | 'm' | 'km' | 'days' | 'hours' | 'months'
  isVerificationEnabled?: boolean
  verificationValue?: number
  verificationUnit?: '' | 'hours' | 'days' | 'months'
  isInternalService?: boolean
  selectedServiceProviders?: string[]
}

interface BackendMachineData {
  code?: string
  name?: string
  model?: string
  manufacturer?: string
  year?: number
  type?: string
  dimension?: string
  weight?: number
  location?: string
  costPerHour?: number
  capacity?: string
  selectedServiceProviders?: string[]
  periodicMaintenances?: BackendPeriodicMaintenanceItem[]
}

const Breadcrumbs: React.FC<{ items: { label: string; path?: string }[] }> = ({
  items,
}) => (
  <BreadcrumbsContainer>
    <ol>
      {items.map((item, index) => (
        <li key={index}>
          {index === items.length - 1 ? (
            <span>{item.label}</span>
          ) : item.path ? (
            <Link to={item.path}>{item.label}</Link>
          ) : (
            <span>{item.label}</span>
          )}
        </li>
      ))}
    </ol>
  </BreadcrumbsContainer>
)

const MaintenanceItemFields: React.FC<{
  item: MaintenanceScheduleItem
  onMaintenanceChange: {
    (
      id: number,
      field: 'isVerificationEnabled' | 'isInternalService',
      value: boolean,
    ): void
    (
      id: number,
      field: 'selectedServiceProviders',
      value: readonly string[],
    ): void
    (
      id: number,
      field:
        | 'periodicityValue'
        | 'periodicityUnit'
        | 'verificationValue'
        | 'verificationUnit'
        | 'maintenanceName',
      value: string,
    ): void
  }
  onRemove: (id: number) => void
  employeeOptions: Option[]
  officeOptions: Option[]
}> = ({
  item,
  onMaintenanceChange,
  onRemove,
  employeeOptions,
  officeOptions,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const currentDropdownOptions = item.isInternalService
    ? employeeOptions
    : officeOptions

  const toggleDropdown = () => {
    if (currentDropdownOptions.length > 0) {
      setDropdownOpen((prev) => !prev)
    }
  }

  const handleServiceProviderClick = (option: Option) => {
    const newValues = [option.value]
    onMaintenanceChange(item.id, 'selectedServiceProviders', newValues)
    setDropdownOpen(false)
  }

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setDropdownOpen(false)
    }
  }, [])

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [handleClickOutside])

  const placeholderText = item.isInternalService
    ? employeeOptions.length > 0
      ? 'Selecione um funcionário'
      : 'Serviço interno, sem responsável'
    : officeOptions.length > 0
    ? 'Selecione uma oficina'
    : 'Nenhuma oficina disponível'

  const displaySelectedText = () => {
    if (item.selectedServiceProviders.length === 0) {
      return (
        <span
          style={{
            color: currentDropdownOptions.length === 0 ? '#696969' : '#C4C4C4',
          }}
        >
          {placeholderText}
        </span>
      )
    } else {
      const selectedOption = currentDropdownOptions.find(
        (opt) => opt.value === item.selectedServiceProviders[0],
      )
      return (
        <span className='dropdown-selected-text'>
          {selectedOption ? selectedOption.label : 'Item selecionado'}
        </span>
      )
    }
  }

  return (
    <StyledMaintenanceItem>
      <NameGroup>
        <label htmlFor={`maintenance-name-${item.id}`}>Nome</label>
        <input
          type='text'
          id={`maintenance-name-${item.id}`}
          value={item.maintenanceName}
          onChange={(e) =>
            onMaintenanceChange(item.id, 'maintenanceName', e.target.value)
          }
          placeholder='Digite aqui'
        />
      </NameGroup>

      <PeriodicidadeGroup>
        <label htmlFor={`periodicity-value-${item.id}`}>Periodicidade</label>
        <div className='input-wrapper'>
          <span className='prefix-text'>A cada</span>
          <input
            type='text'
            id={`periodicity-value-${item.id}`}
            value={item.periodicityValue}
            onChange={(e) =>
              onMaintenanceChange(item.id, 'periodicityValue', e.target.value)
            }
            placeholder='Digite aqui'
          />
          <select
            id={`periodicity-unit-${item.id}`}
            value={item.periodicityUnit}
            onChange={(e) =>
              onMaintenanceChange(
                item.id,
                'periodicityUnit',
                e.target.value as MaintenanceScheduleItem['periodicityUnit'],
              )
            }
          >
            <option value=''>Selecione</option>
            <option value='m'>Metros</option>
            <option value='km'>Quilômetros</option>
            <option value='days'>Dias</option>
            <option value='hours'>Horas</option>
            <option value='months'>Meses</option>
          </select>
        </div>
      </PeriodicidadeGroup>

      <img
        src={rectangle}
        alt='Separador'
        style={{
          alignSelf: 'center',
          height: '100%',
          width: '1px',
          backgroundColor: '#E0E0E0',
          justifySelf: 'center',
        }}
      />

      <VerificationGroup>
        <label htmlFor={`verification-toggle-${item.id}`}>Verificação</label>
        <div className='toggle-content-wrapper'>
          <ToggleSwitchContainer>
            <SwitchLabel
              htmlFor={`verification-toggle-${item.id}`}
              className='verification-switch-align'
            >
              <SwitchInput
                type='checkbox'
                id={`verification-toggle-${item.id}`}
                checked={item.isVerificationEnabled}
                onChange={(e) =>
                  onMaintenanceChange(
                    item.id,
                    'isVerificationEnabled',
                    e.target.checked,
                  )
                }
              />
              <Slider className='slider' />
            </SwitchLabel>
          </ToggleSwitchContainer>

          <div className='verification-fields'>
            <span className='prefix-text'>A cada</span>
            <input
              type='text'
              id={`verification-value-${item.id}`}
              value={item.verificationValue}
              onChange={(e) =>
                onMaintenanceChange(
                  item.id,
                  'verificationValue',
                  e.target.value,
                )
              }
              disabled={!item.isVerificationEnabled}
              placeholder='Digite aqui'
            />
            <select
              id={`verification-unit-${item.id}`}
              value={item.verificationUnit}
              onChange={(e) =>
                onMaintenanceChange(
                  item.id,
                  'verificationUnit',
                  e.target.value as MaintenanceScheduleItem['verificationUnit'],
                )
              }
              disabled={!item.isVerificationEnabled}
            >
              <option value=''>Selecione</option>
              <option value='hours'>Horas</option>
              <option value='days'>Dias</option>
              <option value='months'>Meses</option>
            </select>
          </div>
        </div>
      </VerificationGroup>

      <DeleteButton type='button' onClick={() => onRemove(item.id)}>
        <img src={trash} alt='Excluir' />
      </DeleteButton>

      <MaintenanceServiceWrapper>
        <ToggleSwitchContainer>
          <ToggleSwitchLabelText>Interno</ToggleSwitchLabelText>
          <SwitchLabel
            htmlFor={`item-isInternalService-${item.id}`}
            className='internal-switch-align'
          >
            <SwitchInput
              type='checkbox'
              id={`item-isInternalService-${item.id}`}
              name={`item-isInternalService-${item.id}`}
              checked={item.isInternalService}
              onChange={(e) => {
                onMaintenanceChange(
                  item.id,
                  'isInternalService',
                  e.target.checked,
                )
                onMaintenanceChange(item.id, 'selectedServiceProviders', [])
                setDropdownOpen(false)
              }}
            />
            <Slider className='slider' />
          </SwitchLabel>
        </ToggleSwitchContainer>

        <StyledFormGroup className='flex-grow'>
          <label htmlFor={`item-serviceProviderInput-${item.id}`}>
            Responsável
          </label>
          <DropdownContainer ref={dropdownRef}>
            <input
              type='text'
              readOnly
              value=''
              style={{ position: 'absolute', opacity: 0, height: 0, width: 0 }}
              onFocus={toggleDropdown}
              onBlur={() =>
                setTimeout(() => {
                  if (
                    dropdownRef.current &&
                    !dropdownRef.current.contains(document.activeElement)
                  ) {
                    setDropdownOpen(false)
                  }
                }, 100)
              }
            />

            <DropdownHeader
              className={currentDropdownOptions.length === 0 ? 'disabled' : ''}
              onClick={toggleDropdown}
            >
              {displaySelectedText()}{' '}
            </DropdownHeader>

            {dropdownOpen && currentDropdownOptions.length > 0 && (
              <DropdownList>
                {currentDropdownOptions.map((option) => {
                  const isSelected = item.selectedServiceProviders.includes(
                    option.value,
                  )
                  return (
                    <DropdownListItem
                      key={option.value}
                      onClick={(e) => {
                        e.stopPropagation()
                        handleServiceProviderClick(option)
                      }}
                      className={isSelected ? 'selected' : ''}
                    >
                      <div
                        className={`checkbox-custom ${
                          isSelected ? 'checked' : ''
                        }`}
                      />
                      {option.label}
                    </DropdownListItem>
                  )
                })}
              </DropdownList>
            )}
            {dropdownOpen && currentDropdownOptions.length === 0 && (
              <DropdownList>
                <DropdownListItem
                  style={{
                    cursor: 'default',
                    color: '#999',
                    padding: '8px 12px',
                  }}
                >
                  Nenhuma opção disponível
                </DropdownListItem>
              </DropdownList>
            )}
          </DropdownContainer>
        </StyledFormGroup>
      </MaintenanceServiceWrapper>
    </StyledMaintenanceItem>
  )
}

const EditEquipment = () => {
  const navigate = useNavigate()
  const { machineId: idParam } = useParams<{ machineId: string }>()

  const machineId = idParam ? parseInt(idParam, 10) : null

  const [form, setForm] = useState<FormState>({
    code: '',
    name: '',
    model: '',
    manufacturer: '',
    year: '',
    type: '',
    dimension: '',
    weight: '',
    location: '',
    costPerHour: '',
    capacity: '',
    selectedServiceProviders: [],
  })

  const [maintenanceSchedule, setMaintenanceSchedule] = useState<
    MaintenanceScheduleItem[]
  >([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const employeeOptions: Option[] = [
    { value: 'provider1', label: '123456 - Fernando Fernandes' },
    { value: 'provider2', label: '789012 - Maria Silva' },
    { value: 'provider3', label: '345678 - João Santos' },
    { value: 'provider4', label: '901234 - Ana Costa' },
  ]

  const officeOptions: Option[] = [
    { value: 'office1', label: 'Oficina Mecânica do Zé' },
    { value: 'office2', label: 'Auto Elétrica Avenida' },
  ]

  const globalServiceProviderOptions: Option[] = employeeOptions

  useEffect(() => {
    const fetchMachine = async () => {
      if (machineId === null || isNaN(machineId)) {
        setError('ID da máquina inválido ou não fornecido na URL.')
        setIsLoading(false)
        return
      }

      const token = localStorage.getItem('token')
      if (!token) {
        alert('Sessão expirada ou não logado. Faça login novamente.')
        navigate('/login')
        return
      }

      try {
        const response = await axios.get<BackendMachineData>(
          `${process.env.REACT_APP_EQUIPMENT_BASE_URL}/equipment/${machineId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        )

        const machineData = response.data

        setForm({
          code: machineData.code || '',
          name: machineData.name || '',
          model: machineData.model || '',
          manufacturer: machineData.manufacturer || '',
          year: machineData.year?.toString() || '',
          type: machineData.type || '',
          dimension: machineData.dimension || '',
          weight: machineData.weight?.toString() || '',
          location: machineData.location || '',
          costPerHour: machineData.costPerHour?.toString() || '',
          capacity: machineData.capacity || '',
          selectedServiceProviders: machineData.selectedServiceProviders || [],
        })

        const fetchedMaintenance: MaintenanceScheduleItem[] = (
          machineData.periodicMaintenances || []
        ).map((item: BackendPeriodicMaintenanceItem) => {
          return {
            id: item.id || 0,
            maintenanceName: item.maintenanceName || '',
            periodicityValue: item.periodicityValue?.toString() || '',
            periodicityUnit: item.periodicityUnit || '',
            isVerificationEnabled: item.isVerificationEnabled || false,
            verificationValue: item.isVerificationEnabled
              ? item.verificationValue?.toString() || ''
              : '',
            verificationUnit: item.isVerificationEnabled
              ? item.verificationUnit || ''
              : '',
            isInternalService: item.isInternalService || false,
            selectedServiceProviders: item.selectedServiceProviders || [],
          } as MaintenanceScheduleItem
        })

        let nextClientId =
          (fetchedMaintenance.length > 0
            ? Math.max(...fetchedMaintenance.map((i) => i.id))
            : 0) + 1
        const reMappedMaintenance = fetchedMaintenance.map((item) => ({
          ...item,
          id: nextClientId++,
        }))

        setMaintenanceSchedule(reMappedMaintenance)
        setIsLoading(false)
      } catch (err) {
        console.error('Erro ao buscar dados da máquina:', err)
        setError(
          `Erro ao carregar dados da máquina: ${
            axios.isAxiosError(err)
              ? err.response?.data?.message || err.message
              : 'Erro desconhecido'
          }`,
        )
        setIsLoading(false)
      }
    }

    fetchMachine()
  }, [machineId, navigate])

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { name, value } = e.target
      setForm((prev) => ({ ...prev, [name]: value }))
    },
    [],
  )

  const handleMaintenanceChange = useCallback(
    (id: number, field: keyof MaintenanceScheduleItem, value: any) => {
      setMaintenanceSchedule((prev) =>
        prev.map((item) => {
          if (item.id === id) {
            if (field === 'isVerificationEnabled') {
              return {
                ...item,
                isVerificationEnabled: value as boolean,
                ...(value === false && {
                  verificationValue: '',
                  verificationUnit: '',
                }),
              }
            }
            if (field === 'isInternalService') {
              return {
                ...item,
                isInternalService: value as boolean,
                selectedServiceProviders: [],
              }
            }
            if (field === 'selectedServiceProviders') {
              return {
                ...item,
                selectedServiceProviders: value as readonly string[],
              }
            }
            return { ...item, [field]: value }
          }
          return item
        }),
      )
    },
    [],
  )

  const addMaintenanceItem = useCallback(() => {
    setMaintenanceSchedule((prev) => [
      ...prev,
      {
        id: prev.length > 0 ? Math.max(...prev.map((i) => i.id)) + 1 : 1,
        maintenanceName: '',
        periodicityValue: '',
        periodicityUnit: '',
        isVerificationEnabled: false,
        verificationValue: '',
        verificationUnit: '',
        isInternalService: false,
        selectedServiceProviders: [],
      },
    ])
  }, [])

  const removeMaintenanceItem = useCallback((id: number) => {
    setMaintenanceSchedule((prev) => prev.filter((item) => item.id !== id))
  }, [])

  const [globalDropdownOpen, setGlobalDropdownOpen] = useState(false)
  const globalDropdownRef = useRef<HTMLDivElement>(null)
  const globalInputRef = useRef<HTMLInputElement>(null)

  const toggleGlobalDropdown = useCallback(() => {
    setGlobalDropdownOpen((prev) => !prev)
  }, [])

  const handleGlobalServiceProviderClick = useCallback(
    (option: Option) => {
      const isSelected = form.selectedServiceProviders.includes(option.value)
      const newValues = isSelected
        ? form.selectedServiceProviders.filter((v) => v !== option.value)
        : [...form.selectedServiceProviders, option.value]
      setForm((prev) => ({ ...prev, selectedServiceProviders: newValues }))
    },
    [form.selectedServiceProviders],
  )

  const handleGlobalRemoveTag = useCallback((value: string) => {
    setForm((prev) => ({
      ...prev,
      selectedServiceProviders: prev.selectedServiceProviders.filter(
        (v) => v !== value,
      ),
    }))
  }, [])

  const handleGlobalClickOutside = useCallback((event: MouseEvent) => {
    if (
      globalDropdownRef.current &&
      !globalDropdownRef.current.contains(event.target as Node)
    ) {
      setGlobalDropdownOpen(false)
    }
  }, [])

  useEffect(() => {
    document.addEventListener('mousedown', handleGlobalClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleGlobalClickOutside)
    }
  }, [handleGlobalClickOutside])

  const displayGlobalSelectedText = () => {
    if (form.selectedServiceProviders.length === 0) {
      return (
        <span style={{ color: '#C4C4C4' }}>Selecione uma ou mais opções</span>
      )
    } else {
      return (
        <span className='dropdown-selected-text'>
          {form.selectedServiceProviders.length} selecionado(s)
        </span>
      )
    }
  }

  const renderGlobalDropdown = () => {
    return (
      <DropdownContainer ref={globalDropdownRef}>
        <input
          type='text'
          ref={globalInputRef}
          readOnly
          value=''
          style={{ position: 'absolute', opacity: 0, height: 0, width: 0 }}
          onFocus={toggleGlobalDropdown}
          onBlur={() =>
            setTimeout(() => {
              if (
                globalDropdownRef.current &&
                !globalDropdownRef.current.contains(document.activeElement)
              ) {
                setGlobalDropdownOpen(false)
              }
            }, 100)
          }
        />

        <DropdownHeader
          className={''}
          onClick={() => globalInputRef.current?.focus()}
        >
          {displayGlobalSelectedText()}{' '}
        </DropdownHeader>

        {globalDropdownOpen && (
          <DropdownList>
            {globalServiceProviderOptions.map((option) => {
              const isSelected = form.selectedServiceProviders.includes(
                option.value,
              )
              return (
                <DropdownListItem
                  key={option.value}
                  onClick={(e) => {
                    e.stopPropagation()
                    handleGlobalServiceProviderClick(option)
                  }}
                  className={isSelected ? 'selected' : ''}
                >
                  <div
                    className={`checkbox-custom ${isSelected ? 'checked' : ''}`}
                  />
                  {option.label}
                </DropdownListItem>
              )
            })}
          </DropdownList>
        )}
      </DropdownContainer>
    )
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!machineId) {
      alert('Erro: ID da máquina não encontrado para atualização.')
      return
    }

    if (
      !form.selectedServiceProviders ||
      form.selectedServiceProviders.length === 0
    ) {
      alert('Selecione pelo menos um funcionário habilitado.')
      return
    }

    const token = localStorage.getItem('token')
    if (!token) {
      alert('Sessão expirada ou não logado. Faça login novamente.')
      navigate('/login')
      return
    }

    const dataToSend = {
      code: form.code,
      name: form.name,
      model: form.model,
      manufacturer: form.manufacturer,
      year: form.year ? parseInt(form.year) : undefined,
      type: form.type,
      dimension: form.dimension,
      weight: form.weight ? parseFloat(form.weight) : undefined,
      location: form.location,
      costPerHour: form.costPerHour ? parseFloat(form.costPerHour) : undefined,
      capacity: form.capacity,
      selectedServiceProviders: form.selectedServiceProviders,
      periodicMaintenances: maintenanceSchedule.map((item) => ({
        maintenanceName: item.maintenanceName,
        periodicityValue: item.periodicityValue
          ? parseFloat(item.periodicityValue)
          : 0,
        periodicityUnit: item.periodicityUnit,
        isVerificationEnabled: item.isVerificationEnabled,
        ...(item.isVerificationEnabled && {
          verificationValue: item.verificationValue
            ? parseFloat(item.verificationValue)
            : 0,
          verificationUnit: item.verificationUnit,
        }),
        isInternalService: item.isInternalService,
        selectedServiceProviders: item.selectedServiceProviders,
      })),
    }

    if (!dataToSend.costPerHour && dataToSend.costPerHour !== 0) {
      alert('Custo/Hora é obrigatório e deve ser um número válido.')
      return
    }

    for (const item of dataToSend.periodicMaintenances) {
      if (!item.maintenanceName) {
        alert('O nome da manutenção periódica não pode ser vazio.')
        return
      }
      if (!item.periodicityValue && item.periodicityValue !== 0) {
        alert(
          `O valor da periodicidade para "${item.maintenanceName}" é obrigatório e deve ser um número válido.`,
        )
        return
      }
      if (
        item.isVerificationEnabled &&
        !item.verificationValue &&
        item.verificationValue !== 0
      ) {
        alert(
          `O valor da verificação para "${item.maintenanceName}" é obrigatório e deve ser um número válido.`,
        )
        return
      }
      if (
        !item.selectedServiceProviders ||
        item.selectedServiceProviders.length === 0
      ) {
        alert(
          `Selecione um responsável para a manutenção "${item.maintenanceName}".`,
        )
        return
      }
    }

    try {
      await axios.patch(
        `${process.env.REACT_APP_EQUIPMENT_BASE_URL}/equipment/${machineId}`,
        dataToSend,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        },
      )
      alert('Máquina atualizada com sucesso!')
      navigate('/panel/equipment')
    } catch (error) {
      console.error('Erro ao atualizar máquina:', error)
      alert(
        `Erro ao atualizar máquina: ${
          axios.isAxiosError(error)
            ? error.response?.data?.message || error.message
            : 'Erro na requisição'
        }`,
      )
    }
  }

  const breadcrumbItems = [
    { label: 'Equipamentos', path: '/panel/equipment' },
    { label: 'Editar máquina' },
  ]

  if (isLoading) {
    return (
      <MainContainer>
        <p>Carregando dados da máquina...</p>
      </MainContainer>
    )
  }

  if (error) {
    return (
      <MainContainer>
        <p style={{ color: 'red' }}>{error}</p>
        <CancelButton
          type='button'
          onClick={() => navigate('/panel/equipment')}
        >
          Voltar para Equipamentos
        </CancelButton>
      </MainContainer>
    )
  }

  return (
    <>
      <SubHeaderContainer>
        <LeftSide>
          <SectionName>Editar máquina</SectionName>
        </LeftSide>
      </SubHeaderContainer>

      <Breadcrumbs items={breadcrumbItems} />

      <MainContainer>
        <form onSubmit={handleSubmit}>
          <StyledCard>
            <CardTitle>Identificação</CardTitle>
            <FormGrid cols={3}>
              <StyledFormGroup>
                <label htmlFor='code'>Código</label>
                <input
                  type='text'
                  id='code'
                  name='code'
                  value={form.code}
                  onChange={handleChange}
                  placeholder='Digite aqui'
                />
              </StyledFormGroup>
              <StyledFormGroup>
                <label htmlFor='name'>Nome</label>
                <input
                  type='text'
                  id='name'
                  name='name'
                  value={form.name}
                  onChange={handleChange}
                  placeholder='Digite aqui'
                />
              </StyledFormGroup>
              <StyledFormGroup>
                <label htmlFor='model'>Modelo</label>
                <input
                  type='text'
                  id='model'
                  name='model'
                  value={form.model}
                  onChange={handleChange}
                  placeholder='Digite aqui'
                />
              </StyledFormGroup>
              <StyledFormGroup>
                <label htmlFor='manufacturer'>Fabricante</label>
                <input
                  type='text'
                  id='manufacturer'
                  name='manufacturer'
                  value={form.manufacturer}
                  onChange={handleChange}
                  placeholder='Digite aqui'
                />
              </StyledFormGroup>
              <StyledFormGroup>
                <label htmlFor='year'>Ano</label>
                <input
                  type='text'
                  id='year'
                  name='year'
                  value={form.year}
                  onChange={handleChange}
                  placeholder='Digite aqui'
                />
              </StyledFormGroup>
              <StyledFormGroup>
                <label htmlFor='type'>Tipo</label>
                <select
                  id='type'
                  name='type'
                  value={form.type}
                  onChange={handleChange}
                >
                  <option value=''>Selecione</option>
                  <option value='Veículo'>Veículo</option>
                  <option value='Máquina Fixa'>Máquina Fixa</option>
                  <option value='Equipamento Móvel'>Equipamento Móvel</option>
                </select>
              </StyledFormGroup>
              <StyledFormGroup>
                <label htmlFor='dimension'>Dimensão</label>
                <input
                  type='text'
                  id='dimension'
                  name='dimension'
                  value={form.dimension}
                  onChange={handleChange}
                  placeholder='Digite aqui'
                />
              </StyledFormGroup>
              <StyledFormGroup>
                <label htmlFor='weight'>Peso (kg)</label>
                <input
                  type='number'
                  id='weight'
                  name='weight'
                  value={form.weight}
                  onChange={handleChange}
                  placeholder='Digite aqui'
                />
              </StyledFormGroup>
              <StyledFormGroup>
                <label htmlFor='location'>Localização</label>
                <input
                  type='text'
                  id='location'
                  name='location'
                  value={form.location}
                  onChange={handleChange}
                  placeholder='Digite aqui'
                />
              </StyledFormGroup>
              <StyledFormGroup>
                <label htmlFor='costPerHour'>Custo/Hora (R$)</label>
                <input
                  type='number'
                  id='costPerHour'
                  name='costPerHour'
                  value={form.costPerHour}
                  onChange={handleChange}
                  placeholder='Digite aqui'
                />
              </StyledFormGroup>
              <StyledFormGroup>
                <label htmlFor='capacity'>Capacidade</label>
                <input
                  type='text'
                  id='capacity'
                  name='capacity'
                  value={form.capacity}
                  onChange={handleChange}
                  placeholder='Digite aqui'
                />
              </StyledFormGroup>
            </FormGrid>
            <StyledFormGroup>
              <label>Responsável(is) Habilitado(s)</label>
              <DropdownHeaderSelectedItems>
                {form.selectedServiceProviders.length === 0 ? (
                  renderGlobalDropdown()
                ) : (
                  <>
                    <div className='tags-container'>
                      {form.selectedServiceProviders.map((value) => {
                        const selectedOption =
                          globalServiceProviderOptions.find(
                            (opt) => opt.value === value,
                          )
                        return selectedOption ? (
                          <DropdownSelectedItemTag key={value}>
                            {selectedOption.label}
                            <button
                              type='button'
                              onClick={() => handleGlobalRemoveTag(value)}
                            >
                              x
                            </button>
                          </DropdownSelectedItemTag>
                        ) : null
                      })}
                    </div>
                    {renderGlobalDropdown()}
                  </>
                )}
              </DropdownHeaderSelectedItems>
            </StyledFormGroup>
          </StyledCard>

          <StyledCard>
            <StyledCardHeader>
              <CardTitle>Manutenções Periódicas</CardTitle>
            </StyledCardHeader>
            {maintenanceSchedule.map((item) => (
              <MaintenanceItemFields
                key={item.id}
                item={item}
                onMaintenanceChange={handleMaintenanceChange}
                onRemove={removeMaintenanceItem}
                employeeOptions={employeeOptions}
                officeOptions={officeOptions}
              />
            ))}
            <AddMaintenanceButton type='button' onClick={addMaintenanceItem}>
              <img src={plus} alt='Adicionar' />
              Adicionar manutenção
            </AddMaintenanceButton>
          </StyledCard>

          <ActionButtons>
            <CancelButton
              type='button'
              onClick={() => navigate('/panel/equipment')}
            >
              Cancelar
            </CancelButton>
            <SaveButton type='submit'>
              <img src={save} alt='Salvar' />
              Salvar
            </SaveButton>
          </ActionButtons>
        </form>
      </MainContainer>
    </>
  )
}

export default EditEquipment
